import { useState, useEffect, useCallback } from 'react';
import { Download, Trash2, CheckCircle, Send, Loader2 } from 'lucide-react';
import { rapportsApi } from '@/api';
import useAuthStore from '@/stores/useAuthStore';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import { formatDate } from '@/lib/utils';

const STATUT_BADGES = {
  brouillon: { variant: 'brouillon', label: 'Brouillon' },
  valide: { variant: 'genere', label: 'Validé' },
  remis_au_client: { variant: 'envoye', label: 'Remis au client' },
};

export default function RapportVersionList({ dossierId, refreshKey }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const user = useAuthStore((s) => s.user);

  const canValidate = user?.role === 'admin' || user?.role === 'expert_senior';

  const fetchVersions = useCallback(async () => {
    try {
      const data = await rapportsApi.listVersions(dossierId);
      setVersions(data);
    } catch (err) {
      if (err.status !== 404) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [dossierId]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions, refreshKey]);

  const handleDownload = async (versionId) => {
    setActionLoading(versionId);
    try {
      await rapportsApi.telecharger(versionId);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleStatut = async (versionId, statut) => {
    setActionLoading(versionId);
    setError(null);
    try {
      await rapportsApi.changerStatut(versionId, statut);
      await fetchVersions();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (versionId) => {
    if (!window.confirm('Supprimer cette version brouillon ?')) return;
    setActionLoading(versionId);
    setError(null);
    try {
      await rapportsApi.supprimer(versionId);
      await fetchVersions();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (versions.length === 0) return null;

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-slate-500 uppercase">
        Historique des versions ({versions.length})
      </h3>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      <div className="overflow-hidden border border-slate-200 rounded-lg">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Version</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Format</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Statut</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Généré par</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {versions.map((v) => {
              const badge = STATUT_BADGES[v.statut] || STATUT_BADGES.brouillon;
              const isLoading = actionLoading === v.id;
              return (
                <tr key={v.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">v{v.numero_version}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{formatDate(v.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 uppercase">
                      {v.format_fichier}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{v.genere_par_nom || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {/* Télécharger */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownload(v.id)}
                        disabled={isLoading}
                        title="Télécharger"
                      >
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      </Button>

                      {/* Valider (brouillon → validé) */}
                      {canValidate && v.statut === 'brouillon' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleStatut(v.id, 'valide')}
                          disabled={isLoading}
                          title="Valider"
                        >
                          <CheckCircle className="w-4 h-4 text-carex-600" />
                        </Button>
                      )}

                      {/* Remettre au client (validé → remis_au_client) */}
                      {canValidate && v.statut === 'valide' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleStatut(v.id, 'remis_au_client')}
                          disabled={isLoading}
                          title="Marquer remis au client"
                        >
                          <Send className="w-4 h-4 text-emerald-600" />
                        </Button>
                      )}

                      {/* Supprimer (brouillon uniquement) */}
                      {v.statut === 'brouillon' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(v.id)}
                          disabled={isLoading}
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
